import React from 'react';

import styled from '@emotion/styled';
import Carousel from 'react-slick';
import iconArrowBack from 'src/public/img/icon-arrow-back.svg';
import iconArrowForward from 'src/public/img/icon-arrow-forward.svg';
import { breakPointsValues, mediaQuery } from 'src/shared/ds/variables';
import { pxToRem } from 'src/shared/helpers/pxToRem';
import { color } from 'src/shared/styles/variables';
import { ContentCard, ContentCardItem, ContentCardSkeleton } from './ContentCard';

export interface ContentCardItemWithId extends ContentCardItem {
    id: string;
}

interface ContentCardsRowProps {
    items: ContentCardItemWithId[];
    cardTestID?: string;
    horizontalScrollMode?: boolean;
}

export const ContentCardsRow: React.FC<ContentCardsRowProps> = ({
    items,
    cardTestID = 'ContentCard',
}) => {
    return (
        <StyledCarousel {...sliderSettings}>
            {items.map((item, i) => (
                <CarouselItem key={`slide_${item.title}`}>
                    <ContentCard {...item} key={item.id} testID={cardTestID + i} />
                </CarouselItem>
            ))}
        </StyledCarousel>
    );
};

interface ContentCardsRowSkeletonProps {
    items: unknown[];
}

export const ContentCardsRowSkeleton: React.FC<ContentCardsRowSkeletonProps> = ({ items }) => {
    return (
        <StyledCarousel {...sliderSettings} arrows={false}>
            {items.map((_, i) => (
                <CarouselItem key={`slide_skeleton_${i}`}>
                    <ContentCardSkeleton />
                </CarouselItem>
            ))}
        </StyledCarousel>
    );
};

interface CarouselButtonProps {
    prevButton?: boolean;
    onClick?: () => void;
    className?: string;
}

const CarouselButton: React.FC<CarouselButtonProps> = ({ onClick, className = '', prevButton }) => {
    const xPosField = prevButton ? 'left' : 'right';
    const image = prevButton ? iconArrowBack : iconArrowForward;
    const opacity = className.includes('disabled') ? 0 : 1;

    return (
        <CarouselArrowImage xPosField={xPosField} opacity={opacity} src={image} onClick={onClick} />
    );
};

const StyledCarousel = styled(Carousel)({
    '&': {
        marginBottom: 0,
    },
    '& .slick-track': {
        marginLeft: 0,
        display: 'flex',
        padding: 0,

        '&:before, &:after': {
            content: 'none',
        },
    },
    '& button.slick-arrow': {
        '&::before, &::after': {
            color: color.black,
        },
    },
});

const CarouselItem = styled('div')({
    '&&': {
        display: 'flex',
        cursor: 'pointer',
        height: 'auto',

        '&:not(:first-of-type)': {
            marginLeft: pxToRem(16),
        },
    },
});

interface CarouselArrowImageProps {
    xPosField: string;
    opacity: number;
}

const CarouselArrowImage = styled.img<CarouselArrowImageProps>(({ opacity, xPosField }) => ({
    height: pxToRem(32),
    width: pxToRem(32),
    position: 'absolute',
    top: pxToRem(40),
    cursor: 'pointer',
    [xPosField]: pxToRem(-45),
    opacity,

    [mediaQuery.maxWidth('xl')]: {
        top: pxToRem(24),
    },
}));

const sliderSettings = {
    infinite: false,
    speed: 500,
    dots: false,
    slidesToShow: 5,
    slidesToScroll: 3,
    nextArrow: <CarouselButton />,
    prevArrow: <CarouselButton prevButton />,
    responsive: [
        {
            breakpoint: breakPointsValues.md,
            settings: {
                slidesToShow: 2,
                slidesToScroll: 1,
                arrows: false,
            },
        },
        {
            breakpoint: breakPointsValues.xl,
            settings: {
                slidesToShow: 4,
                slidesToScroll: 1,
                arrows: false,
            },
        },
    ],
};
